import { ref } from 'vue'

/**
 * 简化版任务结果提示管理（taskId 粒度）。
 *
 * 用于分班、选课、选科、选科分班等模块——这些模块没有 runId 概念，
 * 只需要按 taskId 记住"用户已关闭结果条"。
 *
 * 使用 sessionStorage 持久化，页面刷新后关闭状态不丢失，关闭浏览器后自动清除。
 */
export function useTaskOutcomeDismiss(storageKey: string): {
  shouldShowOutcome: (taskId: string) => boolean
  dismissTaskOutcome: (taskId: string) => void
  acknowledgeTaskOutcome: (taskId: string) => void
} {
  const dismissed = ref<Set<string>>(readFromSession(storageKey))

  function persist(): void {
    if (typeof sessionStorage === 'undefined') return
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(Array.from(dismissed.value)))
    } catch {
      // 忽略配额等写入失败
    }
  }

  function shouldShowOutcome(taskId: string): boolean {
    return !dismissed.value.has(taskId)
  }

  function dismissTaskOutcome(taskId: string): void {
    const next = new Set(dismissed.value)
    next.add(taskId)
    dismissed.value = next
    persist()
  }

  /** 用户进入详情/编辑等"已处理"动作时，收起该任务当前结果条 */
  function acknowledgeTaskOutcome(taskId: string): void {
    dismissTaskOutcome(taskId)
  }

  return {
    shouldShowOutcome,
    dismissTaskOutcome,
    acknowledgeTaskOutcome
  }
}

function readFromSession(key: string): Set<string> {
  if (typeof sessionStorage === 'undefined') return new Set()
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((id: unknown) => typeof id === 'string'))
    }
    return new Set()
  } catch {
    return new Set()
  }
}
